import { tr } from '@/lib/i18n-core';
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { IntegrationConnectionDto } from '@his/shared';
import { NPHIES_ELIGIBILITY_VALID_DAYS_DEFAULT, NPHIES_ELIGIBILITY_VALID_DAYS_KEY, NPHIES_ELIGIBILITY_VALID_DAYS_MAX, NPHIES_ELIGIBILITY_VALID_DAYS_MIN } from '@his/shared';
import { get, put } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { Alert, Button, Card, CardContent, CardHeader, CardTitle, Field, Input } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { parseValidDays } from './eligibility-session';

export function EligibilitySettings() {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const mayEdit = can('nphies.settings', 'integration.manage');
  const connections = useQuery({
    queryKey: ['integration-connections'],
    queryFn: () => get<IntegrationConnectionDto[]>('/integrations/connections'),
    enabled: can('nphies.settings', 'integration.manage', 'integration.read'),
  });
  const stored = connections.data?.find((connection) => connection.provider === 'nphies')?.settings[NPHIES_ELIGIBILITY_VALID_DAYS_KEY];
  const [days, setDays] = useState(String(NPHIES_ELIGIBILITY_VALID_DAYS_DEFAULT));

  useEffect(() => {
    if (connections.data) setDays(stored ?? String(NPHIES_ELIGIBILITY_VALID_DAYS_DEFAULT));
  }, [connections.data, stored]);

  const parsed = parseValidDays(days);
  const save = useMutation({
    mutationFn: () => put(`/integrations/connections/nphies`, { settings: { [NPHIES_ELIGIBILITY_VALID_DAYS_KEY]: String(parsed) } }),
    onSuccess: () => {
      toast.success(tr('billing', 'nx_validity_saved'));
      void queryClient.invalidateQueries({ queryKey: ['integration-connections'] });
      void queryClient.invalidateQueries({ queryKey: ['eligibility-sessions'] });
    },
    onError: (caught) => toast.error(tr('billing', 'nx_could_not_save_validity'), describeError(caught)),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">{tr('billing', 'nx_eligibility_settings_title')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">{tr('billing', 'nx_eligibility_settings_hint')}</p>
        {connections.isError && <Alert variant="destructive">{describeError(connections.error)}</Alert>}
        <div className="flex flex-wrap items-end gap-3">
          <Field
            label={tr('billing', 'nx_validity_days')}
            hint={tr('billing', 'nx_validity_days_hint', { min: NPHIES_ELIGIBILITY_VALID_DAYS_MIN, max: NPHIES_ELIGIBILITY_VALID_DAYS_MAX })}
            error={parsed === null && days !== '' ? tr('billing', 'nx_validity_days_invalid', { min: NPHIES_ELIGIBILITY_VALID_DAYS_MIN, max: NPHIES_ELIGIBILITY_VALID_DAYS_MAX }) : undefined}
          >
            <Input className="w-32" inputMode="numeric" value={days} disabled={!mayEdit} onChange={(event) => setDays(event.target.value)} />
          </Field>
          {mayEdit && (
            <Button loading={save.isPending} disabled={parsed === null || String(parsed) === (stored ?? String(NPHIES_ELIGIBILITY_VALID_DAYS_DEFAULT))} onClick={() => save.mutate()}>
              {tr('billing', 'save')}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
